
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User } from '@supabase/supabase-js';
import LoginForm from '@/components/auth/LoginForm';
import SignupForm from '@/components/auth/SignupForm';
import ResetPasswordForm from '@/components/auth/ResetPasswordForm';
import Logo from '@/components/common/Logo';
import { useIsMobile } from '@/hooks/use-mobile';
import { ScrollArea } from '@/components/ui/scroll-area';
import { BrandGradient } from '@/components/ui/brand';
import { Sparkles } from 'lucide-react';

// Define a custom event name for tab switching
export const AUTH_TAB_SWITCH_EVENT = 'auth-tab-switch';

interface AuthPageProps {
  defaultTab?: 'signin' | 'signup' | 'reset';
}

const AuthPage: React.FC<AuthPageProps> = ({ defaultTab = 'signin' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [activeTab, setActiveTab] = useState<'signin' | 'signup' | 'reset'>(defaultTab);
  const isMobile = useIsMobile();
  
  // Initialize with the correct tab based on URL path
  useEffect(() => {
    if (location.pathname === '/forgot-password') {
      setActiveTab('reset');
    } else if (location.pathname === '/signup') {
      setActiveTab('signup');
    } else {
      setActiveTab('signin');
    }
  }, [location.pathname]);

  // Update URL when tab changes without full navigation
  useEffect(() => {
    if (activeTab === 'signin' && location.pathname !== '/login') {
      window.history.replaceState(null, '', '/login');
    } else if (activeTab === 'signup' && location.pathname !== '/signup') {
      window.history.replaceState(null, '', '/signup');
    } else if (activeTab === 'reset' && location.pathname !== '/forgot-password') {
      window.history.replaceState(null, '', '/forgot-password');
    }
  }, [activeTab, location.pathname]);

  // Add event listener for tab switching
  useEffect(() => {
    const handleTabSwitch = (e: CustomEvent) => {
      if (e.detail && typeof e.detail === 'string') {
        setActiveTab(e.detail as 'signin' | 'signup' | 'reset');
      }
    };

    window.addEventListener(AUTH_TAB_SWITCH_EVENT as any, handleTabSwitch as EventListener);

    return () => {
      window.removeEventListener(AUTH_TAB_SWITCH_EVENT as any, handleTabSwitch as EventListener);
    };
  }, []);

  useEffect(() => {
    // Check if user is already signed in
    const checkUser = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          setUser(data.session.user);
          // Check if user has completed onboarding
          const { data: businessData } = await supabase
            .from('business')
            .select('*')
            .eq('owner_id', data.session.user.id)
            .single();
          
          if (businessData) {
            if (businessData.is_onboarding_complete) {
              navigate('/app/dashboard');
            } else {
              navigate('/onboarding');
            }
          } else {
            navigate('/onboarding');
          }
        }
      } catch (error) {
        console.error("Error checking user session:", error);
      }
    };

    checkUser();

    // Set up auth state listener
    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (session) {
          setUser(session.user);
          try {
            // Check if user has completed onboarding
            const { data: businessData } = await supabase
              .from('business')
              .select('*')
              .eq('owner_id', session.user.id)
              .maybeSingle();
            
            if (businessData) {
              if (businessData.is_onboarding_complete) {
                navigate('/app/dashboard');
              } else {
                navigate('/onboarding');
              }
            } else {
              navigate('/onboarding');
            }
          } catch (error) {
            console.error("Error checking business data:", error);
            navigate('/onboarding');
          }
        } else {
          setUser(null);
        }
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate]);

  if (user) {
    return <div className="flex justify-center items-center h-screen">Redirecting...</div>;
  }

  return (
    <BrandGradient
      variant="primary"
      intensity="light"
      className="flex flex-col items-center justify-center min-h-screen p-4 overflow-auto"
    >
      <div className="w-full max-w-md space-y-8 my-4">
        <div className="flex flex-col items-center justify-center mb-8">
          <div className="relative">
            <Logo size="xl" />
            <Sparkles className="absolute -top-2 -right-2 text-primary h-6 w-6 animate-pulse-gentle" />
          </div>
          <h1 className="text-3xl font-bold mt-4 bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent">
            VirtuStaff.ai
          </h1>
          <p className="text-muted-foreground mt-2">Your AI Marketing Assistant</p>
        </div>

        <Card className="border border-primary/10 shadow-lg shadow-primary/5">
          <CardContent className={`${isMobile ? 'p-3' : 'p-6'} pt-6`}>
            <Tabs 
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as 'signin' | 'signup' | 'reset')}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
                <TabsTrigger value="reset">Reset</TabsTrigger>
              </TabsList>
              
              <ScrollArea className={isMobile ? "max-h-[60vh] overflow-y-auto" : undefined}>
                <TabsContent value="signin" className="mt-0">
                  <LoginForm />
                </TabsContent>
                
                <TabsContent value="signup" className="mt-0">
                  <SignupForm />
                </TabsContent>

                <TabsContent value="reset" className="mt-0">
                  <ResetPasswordForm />
                </TabsContent>
              </ScrollArea>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </BrandGradient>
  );
};

export default AuthPage;
